import { useSuspenseQuery } from '@apollo/client/react';
import { useMemo, useRef, useState, useTransition } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { SearchX } from 'lucide-react';
import { debounce } from 'lodash';

import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getGravatarUrl } from 'src/utils/getGravatarUrl';
import { withSuspense } from 'src/hoc';
import { AppMembershipsOrderByEnum, QueryOrderEnum, UserRoleEnum } from 'src/graphql/schema';
import { AppMembershipsDocument } from 'src/graphql/actions/appMemberships/appMemberships.query';
import { ProfileDocument } from 'src/graphql/actions/profile/profile.query';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from 'src/lib/utils';
import Paginator from '@/components/ui/paginator';
import { InputSearch } from '@/components/ui/input-search';
import { EmptyBlock } from '@/components/ui/empty-block';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

import { RoleCell, RemoveMembershipDialog, refetchAppMemberships, getMembershipSortPriority } from '../../Management';
import { MembershipActionsContextProvider, useMembershipActions } from '../model';

import ActionDropdown from './ActionDropdown';
import EditPermissionDialog from './EditPermissionDialog';

type IMembershipRow = {
  id: string;
  user: {
    id: string;
    name: string;
    role: UserRoleEnum;
    email: string;
    isCurrentUser: boolean;
  };
  role: string;
  action: {
    canUpdate: boolean;
    canRemove: boolean;
  };
};

const columnHelper = createColumnHelper<IMembershipRow>();
const getColumns = (totalCount: number) => [
  columnHelper.accessor('user', {
    header: `Member (${totalCount})`,
    cell: (props) => {
      const { name, email, role, isCurrentUser } = props.getValue();
      const fallback = name?.charAt(0).toUpperCase();

      return (
        <div className="flex items-center gap-2">
          <Avatar className="size-8.5 rounded-full shrink-0">
            <AvatarImage src={getGravatarUrl(email)} />
            <AvatarFallback className="rounded-full bg-primary text-white">{fallback}</AvatarFallback>
          </Avatar>
          <div className="grow min-w-0">
            <div className="flex items-center gap-2">
              <div className="text-sm text-foreground font-semibold truncate">{name}</div>
              {role === UserRoleEnum.Owner ? (
                <Badge
                  variant="pill"
                  className="border-0 whitespace-nowrap text-2xs bg-foreground text-white py-0.5 px-1.5"
                >
                  Account owner
                </Badge>
              ) : (
                isCurrentUser && (
                  <Badge variant="pill" className="border-0 whitespace-nowrap text-2xs py-0.5 px-1.5">
                    You
                  </Badge>
                )
              )}
            </div>
            <div className="text-sm text-muted-foreground truncate">{email}</div>
          </div>
        </div>
      );
    },
  }),
  columnHelper.accessor('role', {
    header: 'Permission',
    cell: (props) => <RoleCell id={props.row.original.id} role={props.getValue()} />,
  }),
  columnHelper.accessor('action', {
    header: '',
    meta: {
      headerClassName: 'w-16',
      cellClassName: 'w-16',
    },
    enableResizing: true,
    cell: (props) => (
      <ActionDropdown
        id={props.row.original.id}
        canRemove={props.getValue().canRemove}
        canUpdate={props.getValue().canUpdate}
      />
    ),
  }),
];

type Props = {
  appId: string;
};

const MembershipsViewContainerContent = ({ appId }: Props) => {
  const { removeMembershipId, closeRemoveDialog } = useMembershipActions();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [isPending, startTransition] = useTransition();
  const debouncedCommitSearch = useRef(
    debounce(
      (value: string) =>
        startTransition(() => {
          setSearchQuery(value);
          setPage(1);
        }),
      500,
    ),
  ).current;
  const {
    data: { profile },
  } = useSuspenseQuery(ProfileDocument);
  const {
    data: { appMemberships },
  } = useSuspenseQuery(AppMembershipsDocument, {
    variables: {
      appId,
      page,
      perPage: 10,
      orderBy: AppMembershipsOrderByEnum.Role,
      order: QueryOrderEnum.Asc,
      query: searchQuery,
    },
  });
  const {
    data: {
      appMemberships: { nodesCount: totalMembersCount },
    },
  } = useSuspenseQuery(AppMembershipsDocument, {
    variables: {
      appId,
      perPage: 0,
    },
  });

  const membersMapped = useMemo(() => {
    const sortedNodes = appMemberships.nodes.slice().sort((a, b) => {
      const priorityA = getMembershipSortPriority(a.user, profile.id);
      const priorityB = getMembershipSortPriority(b.user, profile.id);

      if (priorityA !== priorityB) return priorityA - priorityB;

      return 0;
    });

    return sortedNodes.map((membership) => ({
      id: membership.id,
      user: {
        id: membership.user.id,
        name: membership.user.name || '-',
        role: membership.user.role,
        email: membership.user.email || '-',
        isCurrentUser: membership.user.id === profile.id,
      },
      role: membership.role,
      action: {
        canUpdate: membership.policy.update,
        canRemove: membership.policy.remove,
      },
    }));
  }, [appMemberships.nodes, profile.id]);
  const columns = useMemo(() => getColumns(totalMembersCount), [totalMembersCount]);
  const table = useReactTable({
    data: membersMapped,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const onPageChange = (pageNumber: number) => startTransition(() => setPage(pageNumber));

  const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => debouncedCommitSearch(event.target.value);

  const onClearSearch = () => {
    debouncedCommitSearch.cancel();
    startTransition(() => {
      setSearchQuery('');
      setPage(1);
    });
  };

  const onRemoveDialogOpenChange = (open: boolean) => {
    if (!open) closeRemoveDialog();
  };

  return (
    <section className="space-y-3">
      <InputSearch
        isLoading={false}
        placeholder="Search name or email"
        className="max-w-75"
        onChange={onSearch}
        onClear={onClearSearch}
      />
      <Card className="overflow-hidden">
        <Table isLoading={isPending}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      'bg-gray-50 text-slate-700 font-bold text-xs first:rounded-tl-md last:rounded-tr-md uppercase',
                      header.column.columnDef.meta?.headerClassName,
                    )}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="group/row">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        cell.column.columnDef.meta?.cellClassName,
                        'group-last/row:first:rounded-bl-md group-last/row:last:rounded-br-md',
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={table.getAllColumns().length} className="h-4 text-center">
                  <EmptyBlock
                    title="No results"
                    icon={SearchX}
                    withCard={false}
                    classes={{
                      title: 'text-sm',
                    }}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
      <Paginator
        currentPage={page}
        totalPages={appMemberships.pagesCount}
        onPageChange={onPageChange}
        showPreviousNext={true}
        hideOnSinglePage
        className="py-2"
      />
      <RemoveMembershipDialog
        id={removeMembershipId ?? ''}
        open={Boolean(removeMembershipId)}
        onOpenChange={onRemoveDialogOpenChange}
        successCallback={refetchAppMemberships}
      />
      <EditPermissionDialog />
    </section>
  );
};

const MembershipsViewContainer = (props: Props) => (
  <MembershipActionsContextProvider>
    <MembershipsViewContainerContent {...props} />
  </MembershipActionsContextProvider>
);

export default withSuspense(MembershipsViewContainer);
